import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Text,
  Heading,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Alert,
  AlertIcon,
  useColorModeValue,
  Checkbox,
  Button,
  ButtonGroup,
} from '@chakra-ui/react';
import fetchWithAuth from '../utils/fetchWithAuth';

function TargetDemographicsForm({ populationId, audienceId, onChange }) {
  const [demographicFields, setDemographicFields] = useState([]);
  const [targetPercentages, setTargetPercentages] = useState({});
  const [enabledDemographics, setEnabledDemographics] = useState({});
  const [responseGoal, setResponseGoal] = useState(500);
  const [defaultTrancheSize, setDefaultTrancheSize] = useState(100);
  const [loading, setLoading] = useState(true);

  const bgColor = useColorModeValue('background.shade2');
  const borderColor = useColorModeValue('gray.300');

  useEffect(() => {
    if (audienceId) {
      fetchDemographicFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audienceId]);

  useEffect(() => {
    // Calculate smart defaults when audience changes
    if (audienceId) {
      fetchAudienceAndCalculateDefaults();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audienceId]);

  const fetchAudienceAndCalculateDefaults = async () => {
    try {
      const response = await fetchWithAuth(`/api/audiences/${audienceId}/`);
      const audience = await response.json();
      const audienceSize = audience.subframe_size || 0;

      if (audienceSize > 0) {
        // Import the same calculation functions used in StagingPoll
        const { calculateSampleSize, calculateInitialTranche } = await import('../utils/sampleSize');
        const sampleSize = calculateSampleSize(audienceSize);
        const tranche = calculateInitialTranche(sampleSize);

        setResponseGoal(sampleSize);
        setDefaultTrancheSize(tranche);
      }
    } catch (error) {
      console.error('Error fetching audience for defaults:', error);
    }
  };

  useEffect(() => {
    // Notify parent component of changes
    // Convert to new structure: {fieldName: {enabled: bool, targets: {...}}}
    if (onChange) {
      const formattedDemographics = {};
      Object.keys(targetPercentages).forEach(fieldName => {
        formattedDemographics[fieldName] = {
          enabled: enabledDemographics[fieldName] !== undefined ? enabledDemographics[fieldName] : true,
          targets: targetPercentages[fieldName],
        };
      });

      onChange({
        target_demographics: formattedDemographics,
        response_goal: responseGoal,
        default_tranche_size: defaultTrancheSize,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetPercentages, enabledDemographics, responseGoal, defaultTrancheSize]);

  const fetchDemographicFields = async () => {
    try {
      // Fetch demographic distribution for the specific audience
      // This respects subframe_filters and max_pool_size
      const response = await fetchWithAuth(`/api/audiences/${audienceId}/demographics/`);
      const data = await response.json();

      if (data.demographic_breakdowns && Object.keys(data.demographic_breakdowns).length > 0) {
        const demographicBreakdowns = data.demographic_breakdowns;

        // Backend already filtered fields - just convert API format to internal format
        const fieldCounts = {}; // We'll use percentages directly from API
        const fieldsToUse = [];

        Object.keys(demographicBreakdowns).forEach(fieldName => {
          fieldCounts[fieldName] = {};
          const values = [];

          const breakdown = demographicBreakdowns[fieldName];
          Object.keys(breakdown).forEach(value => {
            values.push(value);
            // Store the percentage directly (API already did smart rounding)
            fieldCounts[fieldName][value] = breakdown[value];
          });

          // Add to fields list (backend already filtered, no need to filter again)
          fieldsToUse.push({
            name: fieldName,
            values: values,
          });
        });

        setDemographicFields(fieldsToUse);

        // Initialize with ACTUAL audience distribution (already calculated by backend)
        const initialPercentages = {};
        const initialEnabled = {};
        fieldsToUse.forEach(field => {
          initialPercentages[field.name] = {};
          initialEnabled[field.name] = false; // All demographics disabled by default

          field.values.forEach(value => {
            // Use the percentage directly from the API (already smart-rounded to sum to 100%)
            initialPercentages[field.name][value] = fieldCounts[field.name][value] || 0;
          });
        });

        setTargetPercentages(initialPercentages);
        setEnabledDemographics(initialEnabled);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching demographic fields:', error);
      setLoading(false);
    }
  };

  const handlePercentageChange = (fieldName, value, percentage) => {
    setTargetPercentages(prev => ({
      ...prev,
      [fieldName]: {
        ...prev[fieldName],
        [value]: parseFloat(percentage) || 0,
      },
    }));
  };

  const handleToggleEnabled = (fieldName) => {
    setEnabledDemographics(prev => ({
      ...prev,
      [fieldName]: !prev[fieldName],
    }));
  };

  const handleSelectAll = () => {
    const updated = {};
    demographicFields.forEach(field => { updated[field.name] = true; });
    setEnabledDemographics(updated);
  };

  const handleDeselectAll = () => {
    const updated = {};
    demographicFields.forEach(field => { updated[field.name] = false; });
    setEnabledDemographics(updated);
  };

  const calculateTotal = (fieldName) => {
    if (!targetPercentages[fieldName]) return 0;
    return Object.values(targetPercentages[fieldName]).reduce((sum, val) => sum + val, 0);
  };

  const isFieldValid = (fieldName) => {
    const total = calculateTotal(fieldName);
    return Math.abs(total - 100) < 0.1; // Allow for small floating point errors
  };

  if (loading) {
    return <Text>Analyzing population demographics...</Text>;
  }

  if (demographicFields.length === 0) {
    return (
      <Alert status="warning">
        <AlertIcon />
        No demographic data found in population
      </Alert>
    );
  }

  return (
    <Box bg={bgColor} p={6} borderRadius="md" border="1px solid" borderColor={borderColor}>
      <VStack spacing={6} align="stretch">
        <Heading as="h3" size="md">
          Target Demographics Configuration
        </Heading>

        <Alert status="info" mb={4}>
          <AlertIcon />
          <Box>
            <Text fontSize="sm" fontWeight="bold">
              Default values show the current population distribution.
            </Text>
            <Text fontSize="sm" mt={1}>
              Use checkboxes to select which demographics should be weighted in tranche generation.
              Only enabled demographics will be used to balance your sample.
            </Text>
            <Text fontSize="sm" mt={1}>
              Adjust percentages to set your target demographic goals for enabled fields.
              The system will adaptively weight future tranches to match these targets.
            </Text>
          </Box>
        </Alert>

        {/* Response Goal and Tranche Size */}
        <HStack spacing={4}>
          <FormControl>
            <FormLabel>Response Goal</FormLabel>
            <NumberInput
              value={responseGoal}
              onChange={(valueString) => setResponseGoal(parseInt(valueString) || 0)}
              min={1}
              max={100000}
            >
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
          </FormControl>

          <FormControl>
            <FormLabel>Default Tranche Size</FormLabel>
            <NumberInput
              value={defaultTrancheSize}
              onChange={(valueString) => setDefaultTrancheSize(parseInt(valueString) || 0)}
              min={1}
              max={10000}
            >
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
          </FormControl>
        </HStack>

        {/* Select All / Deselect All */}
        <HStack justify="flex-end">
          <ButtonGroup size="sm" variant="outline">
            <Button onClick={handleSelectAll}>Select All</Button>
            <Button onClick={handleDeselectAll}>Deselect All</Button>
          </ButtonGroup>
        </HStack>

        {/* Demographic Fields */}
        {demographicFields.map(field => (
          <Box
            key={field.name}
            p={4}
            bg="white"
            borderRadius="md"
            border="2px solid"
            borderColor={enabledDemographics[field.name] ? 'blue.400' : borderColor}
            opacity={enabledDemographics[field.name] ? 1 : 0.6}
          >
            <VStack align="stretch" spacing={3}>
              <HStack justify="space-between" align="flex-start">
                <HStack spacing={3}>
                  <Checkbox
                    isChecked={enabledDemographics[field.name]}
                    onChange={() => handleToggleEnabled(field.name)}
                    colorScheme="blue"
                    size="lg"
                  />
                  <VStack align="start" spacing={0}>
                    <Heading as="h4" size="sm" textTransform="capitalize">
                      {field.name.replace(/_/g, ' ')}
                    </Heading>
                    <Text fontSize="xs" color="gray.500">
                      {enabledDemographics[field.name]
                        ? 'Enabled - will be weighted in tranche generation'
                        : 'Disabled - for reference only'}
                    </Text>
                  </VStack>
                </HStack>
                <Text
                  fontSize="sm"
                  fontWeight="bold"
                  color={isFieldValid(field.name) ? 'green.500' : 'red.500'}
                >
                  Total: {calculateTotal(field.name).toFixed(1)}%
                  {isFieldValid(field.name) ? ' ✓' : ' ✗'}
                </Text>
              </HStack>

              {field.values.map(value => (
                <HStack key={value} spacing={4}>
                  <Text flex="1" fontSize="sm">
                    {value}:
                  </Text>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    width="100px"
                    value={targetPercentages[field.name]?.[value] || 0}
                    onChange={(e) => handlePercentageChange(field.name, value, e.target.value)}
                  />
                  <Text fontSize="sm" width="20px">%</Text>
                </HStack>
              ))}

              {!isFieldValid(field.name) && (
                <Alert status="error" size="sm">
                  <AlertIcon />
                  Percentages must sum to 100%
                </Alert>
              )}
            </VStack>
          </Box>
        ))}
      </VStack>
    </Box>
  );
}

export default TargetDemographicsForm;
